import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appAnalyticsApi, type AppAnalyticsDashboard } from '@/api/appAnalytics';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold brand-stat-value tabular-nums">{value}</p>
    </div>
  );
}

function fmtRupee(n: number) {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function currentIstYearMonth() {
  const d = new Date();
  const year = Number(
    new Intl.DateTimeFormat('en', { timeZone: 'Asia/Kolkata', year: 'numeric' }).format(d)
  );
  const month = Number(
    new Intl.DateTimeFormat('en', { timeZone: 'Asia/Kolkata', month: 'numeric' }).format(d)
  );
  return { year, month };
}

function formatTrendLabel(isoDate: string, granularity: 'day' | 'month') {
  if (granularity === 'month') {
    const [, m] = isoDate.split('-');
    const mi = Number(m);
    return `${MONTH_SHORT[mi - 1] ?? m}`;
  }
  const [, m, day] = isoDate.split('-');
  return `${day}/${m}`;
}

function DailyTrendChart({
  title,
  description,
  series,
  granularity = 'day',
  valueFormatter,
}: {
  title: string;
  description?: string;
  series: Array<{ date: string; value: number }>;
  granularity?: 'day' | 'month';
  valueFormatter?: (v: number) => string;
}) {
  const g = granularity;
  const chartData = series.map((p) => ({
    day: formatTrendLabel(p.date, g),
    value: p.value,
  }));
  const fmt = valueFormatter ?? ((v: number) => String(v));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => fmt(Number(v))} />
            <Tooltip formatter={(v: number) => fmt(v)} labelFormatter={(l) => `Date: ${l}`} />
            <Line type="monotone" dataKey="value" stroke="#0d9488" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function FunnelBar({ data }: { data: AppAnalyticsDashboard['engagement']['funnel'] }) {
  const steps = [
    { key: 'login', label: 'Login / open app' },
    { key: 'viewedReport', label: 'Viewed reports' },
    { key: 'bookedAppointment', label: 'Booked appointment' },
    { key: 'redeemedPoints', label: 'Redeemed points' },
  ] as const;
  const max = Math.max(...steps.map((s) => data[s.key]), 1);
  return (
    <div className="space-y-3">
      {steps.map((s) => (
        <div key={s.key}>
          <div className="flex justify-between text-sm mb-1">
            <span>{s.label}</span>
            <span className="font-medium tabular-nums">{data[s.key]}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-teal-600 rounded-full"
              style={{ width: `${(data[s.key] / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AppAnalyticsPage() {
  const istNow = useMemo(() => currentIstYearMonth(), []);
  const [trendYear, setTrendYear] = useState(istNow.year);
  const [trendMonth, setTrendMonth] = useState<number | 'all'>(istNow.month);

  const trendMonthParam = trendMonth === 'all' ? null : trendMonth;

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['app-analytics', trendYear, trendMonth],
    queryFn: () =>
      appAnalyticsApi.getDashboard({
        year: trendYear,
        month: trendMonthParam,
      }),
  });

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = istNow.year; y >= istNow.year - 5; y--) years.push(y);
    return years;
  }, [istNow.year]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <p className="text-destructive p-6">
        Could not load app analytics. Ensure the database migration for patient app events has been applied.
      </p>
    );
  }

  const ex = data.executive;

  return (
    <div className="max-w-6xl space-y-6 pb-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold brand-page-title">App analytics</h1>
          <p className="text-muted-foreground mt-1">
            Patient engagement, loyalty, retention, referrals, and revenue — app users only (at least one
            mobile sign-in). Points are credited only after first app login: on first login we retro-credit
            up to the last 2 settled bills before login; after that, at most 2 additional settled bills per
            patient earn points. Metrics use activity on or after each patient&apos;s first app login only.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Updated {new Date(data.generatedAt).toLocaleString()}
            {isFetching && !isLoading ? ' · Refreshing…' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Select value={String(trendYear)} onValueChange={(v) => setTrendYear(Number(v))}>
            <SelectTrigger className="w-[110px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={trendMonth === 'all' ? 'all' : String(trendMonth)}
            onValueChange={(v) => setTrendMonth(v === 'all' ? 'all' : Number(v))}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Full year</SelectItem>
              {MONTH_SHORT.map((label, i) => (
                <SelectItem key={label} value={String(i + 1)}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {data.dailyTrends && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">
            {data.dailyTrends.period.granularity === 'day' ? 'Daily' : 'Monthly'} trends —{' '}
            {data.dailyTrends.period.label} (IST)
          </h2>
          <div className="grid gap-4 lg:grid-cols-2">
            <DailyTrendChart
              title="New app users"
              description={
                data.dailyTrends.period.granularity === 'day'
                  ? 'First mobile app login (download + sign-in) per day'
                  : 'New app users per month'
              }
              series={data.dailyTrends.appDownloads}
              granularity={data.dailyTrends.period.granularity}
            />
            <DailyTrendChart
              title={
                data.dailyTrends.period.granularity === 'day'
                  ? 'Daily active users (DAU)'
                  : 'Monthly active users'
              }
              description="Distinct app users with login or screen activity"
              series={data.dailyTrends.dau}
              granularity={data.dailyTrends.period.granularity}
            />
            <DailyTrendChart
              title="Points earned"
              description="Loyalty points issued to app users only"
              series={data.dailyTrends.pointsEarned}
              granularity={data.dailyTrends.period.granularity}
            />
            <DailyTrendChart
              title="Points redeemed"
              description="Points burned by app users"
              series={data.dailyTrends.pointsRedeemed}
              granularity={data.dailyTrends.period.granularity}
            />
            <DailyTrendChart
              title="Revenue (app users)"
              description="Completed bill payments from patients who use the app"
              series={data.dailyTrends.revenue}
              granularity={data.dailyTrends.period.granularity}
              valueFormatter={(v) => `₹${Math.round(v).toLocaleString('en-IN')}`}
            />
            <DailyTrendChart
              title="Family members added"
              description="New family links created in the app"
              series={data.dailyTrends.familyMembersAdded}
              granularity={data.dailyTrends.period.granularity}
            />
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="MAU (30d)" value={ex.activePatientsMau} />
        <Stat label="DAU (today)" value={ex.activePatientsDau} />
        <Stat label="DAU / MAU" value={`${ex.dauMauRatioPct}%`} />
        <Stat label="Repeat patients" value={`${ex.repeatPatientPct}%`} />
        <Stat label="App users (OTP or password)" value={ex.appUsersWithLogin} />
        <Stat label="Referral conversion" value={`${ex.referralConversionPct}%`} />
        <Stat label="Points liability" value={fmtRupee(ex.pointsLiabilityRupee)} />
        <Stat label="Revenue via app users (365d)" value={fmtRupee(ex.revenueFromAppUsers365)} />
      </div>

      <Tabs defaultValue="engagement" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="loyalty">Loyalty</TabsTrigger>
          <TabsTrigger value="retention">Retention</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
          <TabsTrigger value="predictive">Predictive</TabsTrigger>
        </TabsList>

        <TabsContent value="engagement" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Engagement (30 days)</CardTitle>
                <CardDescription>
                  MAU, DAU, sessions — patients with at least one app login (OTP or password)
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <Stat label="MAU" value={data.engagement.mau} />
                <Stat label="DAU" value={data.engagement.dau} />
                <Stat label="Sessions" value={data.engagement.sessionsLast30d} />
                <Stat label="Avg sessions / MAU" value={data.engagement.avgSessionsPerMau} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Conversion funnel</CardTitle>
                <CardDescription>Login → report → book → redeem</CardDescription>
              </CardHeader>
              <CardContent>
                <FunnelBar data={data.engagement.funnel} />
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Screen / feature usage</CardTitle>
            </CardHeader>
            <CardContent>
              {data.engagement.featureUsage.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No feature events yet — usage appears as patients open wallet, records, loyalty, and book OPD in the app.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Feature</TableHead>
                      <TableHead className="text-right">Events (30d)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.engagement.featureUsage.map((f) => (
                      <TableRow key={f.eventType}>
                        <TableCell>{f.screen}</TableCell>
                        <TableCell className="text-right tabular-nums">{f.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="loyalty" className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Points issued (all time)" value={data.loyalty.totalPointsIssued} />
            <Stat label="Points redeemed" value={data.loyalty.totalPointsRedeemed} />
            <Stat label="Redemption rate" value={`${data.loyalty.redemptionRatePct}%`} />
            <Stat label="Expiring (30d)" value={data.loyalty.expiringPointsNext30d} />
            <Stat label="Earned (30d)" value={data.loyalty.earnPointsLast30d} />
            <Stat label="Burned (30d)" value={data.loyalty.burnPointsLast30d} />
            <Stat label="Avg points / wallet" value={data.loyalty.avgPointsPerWallet} />
            <Stat label="Liability (₹)" value={fmtRupee(data.loyalty.liabilityRupee)} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tier distribution</CardTitle>
              <CardDescription>App users with a membership tier · revenue after first app login</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Members</TableHead>
                    <TableHead className="text-right">Revenue (365d)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.loyalty.tierDistribution.map((t) => {
                    const rev = data.loyalty.tierRevenue.find((r) => r.tier === t.tier);
                    return (
                      <TableRow key={t.tier}>
                        <TableCell>{t.tier}</TableCell>
                        <TableCell className="text-right">{t.count}</TableCell>
                        <TableCell className="text-right">
                          {rev ? fmtRupee(rev.revenue) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="retention" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Repeat visit rate</CardTitle>
              <CardDescription>OPD patients with 2+ visits in window</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {data.retention.repeatVisitRates.map((r) => (
                <Stat
                  key={r.windowDays}
                  label={`${r.windowDays} days`}
                  value={`${r.ratePct}% (${r.repeatPatients})`}
                />
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Specialty retention (365d)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Speciality</TableHead>
                    <TableHead className="text-right">Patients</TableHead>
                    <TableHead className="text-right">Repeat</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.retention.specialtyRetention.map((s) => (
                    <TableRow key={s.specialisation}>
                      <TableCell>{s.specialisation}</TableCell>
                      <TableCell className="text-right">{s.patients}</TableCell>
                      <TableCell className="text-right">{s.repeatPatients}</TableCell>
                      <TableCell className="text-right">{s.retentionPct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cohort analysis</CardTitle>
              <CardDescription>New patients by month → returned next 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cohort</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead className="text-right">Returned</TableHead>
                    <TableHead className="text-right">Retention</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.retention.cohortAnalysis.map((c) => (
                    <TableRow key={c.cohortMonth}>
                      <TableCell>{c.cohortMonth}</TableCell>
                      <TableCell className="text-right">{c.cohortSize}</TableCell>
                      <TableCell className="text-right">{c.returnedNext30d}</TableCell>
                      <TableCell className="text-right">{c.retentionPct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals" className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Total referrals" value={data.referrals.total} />
            <Stat label="Conversion" value={`${data.referrals.conversionPct}%`} />
            <Stat label="Super referrers (5+)" value={data.referrals.superReferrers.length} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top referrers</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>UHID</TableHead>
                    <TableHead className="text-right">Referrals</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.referrals.topReferrers.map((r) => (
                    <TableRow key={r.patientId}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.uhid ?? '—'}</TableCell>
                      <TableCell className="text-right">{r.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="App user revenue (365d)"
              value={fmtRupee(data.revenue.revenueAppUsers365)}
            />
            <Stat label="Memberships expiring (30d)" value={data.revenue.membershipExpiring30d} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Wallet balances (liability)</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Points" value={data.revenue.wallet.pointsBalance} />
              <Stat label="Cashback" value={fmtRupee(data.revenue.wallet.cashbackBalance)} />
              <Stat label="Promo" value={fmtRupee(data.revenue.wallet.promoBalance)} />
              <Stat label="Refund wallet" value={fmtRupee(data.revenue.wallet.refundBalance)} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Family accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {data.family.linkedAccounts} linked family relationships · Pooling{' '}
                {data.family.poolingEnabled ? 'enabled' : 'disabled'}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operations" className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Appointments (30d)" value={data.operations.appointments30d.total} />
            <Stat label="Pending rate" value={`${data.operations.appointments30d.pendingRatePct}%`} />
            <Stat label="Cancel rate" value={`${data.operations.appointments30d.cancellationRatePct}%`} />
            <Stat label="App bookings tracked" value={data.operations.appointments30d.appBookingsTracked} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Campaigns & notifications (30d)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                In-app deliveries: {data.campaigns.inAppDeliveries30d} · Read: {data.campaigns.inAppRead30d} ·
                Open rate: {data.campaigns.inAppOpenRatePct}%
              </p>
              <p className="text-muted-foreground">WhatsApp campaign messages by status:</p>
              <ul className="list-disc pl-5">
                {data.campaigns.whatsappByStatus.map((w) => (
                  <li key={w.status}>
                    {w.status}: {w.count}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">App adoption by department</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Bookings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.operations.departmentAppAdoption.map((d) => (
                    <TableRow key={d.department}>
                      <TableCell>{d.department}</TableCell>
                      <TableCell className="text-right">{d.bookings}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="predictive" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Heuristic intelligence hooks — upgrade to ML models as event volume grows ({data.predictive.hooks.churnModel}).
          </p>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Churn risk</CardTitle>
                <CardDescription>No visit in 180d (had visit in last 2y)</CardDescription>
              </CardHeader>
              <CardContent className="max-h-64 overflow-auto">
                <ul className="text-sm space-y-2">
                  {data.predictive.churnRiskPatients.map((p) => (
                    <li key={p.patientId}>
                      {p.name} · last {new Date(p.lastVisit).toLocaleDateString()}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">High LTV patients</CardTitle>
              </CardHeader>
              <CardContent className="max-h-64 overflow-auto">
                <ul className="text-sm space-y-2">
                  {data.predictive.highLtvPatients.map((p) => (
                    <li key={p.patientId}>
                      {p.name} · {fmtRupee(p.revenue365)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Follow-up risk</CardTitle>
              <CardDescription>Scheduled / no-show in last 14 days</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.predictive.followUpRiskAppointments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.patientName}</TableCell>
                      <TableCell>{new Date(a.scheduledAt).toLocaleString()}</TableCell>
                      <TableCell>{a.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">{data.benchmarking.note}</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
